import type { AxiosInstance } from 'axios';
import type { SigmaTab } from '@trinity/management-app-client';
import type {
  CreateEntityDashboardBody,
  EntityPage,
  EntityPageOwner,
  UpdateEntityDashboardBody,
} from '../types/entity-pages.js';
import { EntityPageResponseSchema, EntityPageSigmaUrlResponseSchema } from '../types/entity-pages.js';

function ownerPath(owner: EntityPageOwner): string {
  return owner.type === 'project' ? `/projects/${owner.id}` : `/workspaces/brands/${owner.id}`;
}

/** Pages attached to a project or brand. The tabs those screens show are `getProjectTabs` / `getBrandTabs`. */
export class EntityPagesClient {
  constructor(private authenticatedClient: AxiosInstance) {}

  getPage = (owner: EntityPageOwner, pageIdentifier: string): Promise<EntityPage> => {
    return this.authenticatedClient
      .get<unknown>(`${ownerPath(owner)}/pages/${pageIdentifier}`)
      .then((res) => EntityPageResponseSchema.parse(res.data).page);
  };

  createDashboard = (owner: EntityPageOwner, body: CreateEntityDashboardBody): Promise<EntityPage> => {
    return this.authenticatedClient
      .post<unknown>(`${ownerPath(owner)}/pages`, body)
      .then((res) => EntityPageResponseSchema.parse(res.data).page);
  };

  updateDashboard = (
    owner: EntityPageOwner,
    pageIdentifier: string,
    body: UpdateEntityDashboardBody
  ): Promise<EntityPage> => {
    return this.authenticatedClient
      .patch<unknown>(`${ownerPath(owner)}/pages/${pageIdentifier}`, body)
      .then((res) => EntityPageResponseSchema.parse(res.data).page);
  };

  deletePage = (owner: EntityPageOwner, pageIdentifier: string): Promise<void> => {
    return this.authenticatedClient
      .delete(`${ownerPath(owner)}/pages/${pageIdentifier}`)
      .then(() => undefined);
  };

  publishPage = (owner: EntityPageOwner, pageIdentifier: string): Promise<EntityPage> => {
    return this.authenticatedClient
      .patch<unknown>(`${ownerPath(owner)}/pages/${pageIdentifier}/publish`)
      .then((res) => EntityPageResponseSchema.parse(res.data).page);
  };

  unpublishPage = (owner: EntityPageOwner, pageIdentifier: string): Promise<EntityPage> => {
    return this.authenticatedClient
      .patch<unknown>(`${ownerPath(owner)}/pages/${pageIdentifier}/unpublish`)
      .then((res) => EntityPageResponseSchema.parse(res.data).page);
  };

  /** A signed embed URL for one tab of a Sigma page. Short-lived: fetch it when the tab is shown. */
  getSigmaUrl = (owner: EntityPageOwner, pageIdentifier: string, tab: SigmaTab): Promise<string> => {
    return this.authenticatedClient
      .get<unknown>(`${ownerPath(owner)}/pages/${pageIdentifier}/sigma-url`, { params: { tab } })
      .then((res) => EntityPageSigmaUrlResponseSchema.parse(res.data).url);
  };
}
